import React, { useState, useEffect } from 'react';
import { pagesAPI } from '../utils/api';

const Pages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('ru');
  const [contentRu, setContentRu] = useState({});
  const [contentEn, setContentEn] = useState({});

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const data = await pagesAPI.getAll();
      setPages(data);
    } catch (error) {
      console.error('Failed to load pages:', error);
      showNotification('Ошибка загрузки страниц', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleOpenModal = async (pageKey) => {
    try {
      const page = await pagesAPI.getByKey(pageKey);
      setSelectedPage(page);
      setContentRu(page.content_ru || {});
      setContentEn(page.content_en || {});
      setShowModal(true);
    } catch (error) {
      console.error('Failed to load page:', error);
      showNotification('Ошибка загрузки страницы', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPage(null);
    setContentRu({});
    setContentEn({});
    setActiveTab('ru');
  };

  const handleContentChange = (lang, key, value) => {
    if (lang === 'ru') {
      setContentRu(prev => ({ ...prev, [key]: value }));
    } else {
      setContentEn(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await pagesAPI.update(selectedPage.page_key, {
        content_ru: contentRu,
        content_en: contentEn,
        is_active: true
      });
      showNotification('Страница успешно обновлена');
      handleCloseModal();
      loadPages();
    } catch (error) {
      console.error('Failed to save page:', error);
      showNotification('Ошибка сохранения страницы', 'error');
    }
  };

  const getPageTitle = (pageKey) => {
    const titles = {
      hero: 'Главная (Hero)',
      process: 'Процесс работы',
      advantages: 'Преимущества',
      contact: 'Контакты',
      footer: 'Футер'
    };
    return titles[pageKey] || pageKey;
  };

  const renderContentFields = (content, lang) => {
    const keys = Object.keys(content);
    
    return keys.map((key) => {
      const value = content[key];
      
      // Handle nested objects (for complex structures)
      if (typeof value === 'object' && !Array.isArray(value)) {
        return (
          <div key={key} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-gray-700 capitalize">{key}</h4>
            {Object.keys(value).map((subKey) => (
              <div key={subKey}>
                <label className="block text-sm font-medium text-gray-600 mb-1 capitalize">
                  {subKey}
                </label>
                <input
                  type="text"
                  value={value[subKey] || ''}
                  onChange={(e) => {
                    const newContent = { ...content, [key]: { ...value, [subKey]: e.target.value } };
                    if (lang === 'ru') setContentRu(newContent);
                    else setContentEn(newContent);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            ))}
          </div>
        );
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        return (
          <div key={key} className="border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
              {key}
            </label>
            <p className="text-sm text-gray-500">Редактирование массивов пока не поддерживается</p>
          </div>
        );
      }
      
      // Handle strings - use textarea for long text
      return (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
            {key}
          </label>
          {value && value.length > 100 ? (
            <textarea
              value={value}
              onChange={(e) => handleContentChange(lang, key, e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          ) : (
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleContentChange(lang, key, e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          )}
        </div>
      );
    });
  };

  if (loading) {
    return <div className="text-center py-12">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Управление страницами</h1>
        <p className="text-gray-600 mt-2">Редактирование контента страниц сайта</p>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`p-4 rounded-lg ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Pages Grid */}
      {pages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pages.map((page) => (
            <div key={page.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-bold text-gray-900">{getPageTitle(page.page_key)}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  page.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {page.is_active ? 'Активна' : 'Неактивна'}
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Ключ: <span className="font-mono text-gray-900">{page.page_key}</span>
              </p>
              <button
                onClick={() => handleOpenModal(page.page_key)}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-medium transition"
              >
                Редактировать
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-gray-500">Страницы не найдены в базе данных</p>
          <p className="text-gray-400 text-sm mt-2">Запустите seed_database.py для заполнения</p>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && selectedPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Редактировать: {getPageTitle(selectedPage.page_key)}
              </h2>
              <p className="text-gray-600 mt-1">
                Ключ страницы: <span className="font-mono">{selectedPage.page_key}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Language Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => setActiveTab('ru')}
                    className={`px-6 py-3 font-medium ${
                      activeTab === 'ru'
                        ? 'text-orange-600 border-b-2 border-orange-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Русский (RU)
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('en')}
                    className={`px-6 py-3 font-medium ${
                      activeTab === 'en'
                        ? 'text-orange-600 border-b-2 border-orange-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    English (EN)
                  </button>
                </div>
              </div>

              {/* Content Fields */}
              <div className="p-6 space-y-4">
                {activeTab === 'ru' ? (
                  <div className="space-y-4">
                    {renderContentFields(contentRu, 'ru')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {renderContentFields(contentEn, 'en')}
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 p-6 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition"
                >
                  Сохранить изменения
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pages;